import { type DraggableSyntheticListeners } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  createContext,
  useMemo,
} from 'react';

import InfoPopover from '@/components/InfoPopover';

import ListItem from '../List/ListItem';
import DraggableHandle from './DraggableHandle';

type Props = PropsWithChildren<{
  style?: CSSProperties;
  id: string;
  infoPopover?: {
    title?: ReactNode;
    body: ReactNode;
  };
}>;

type SortableListContext = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
};

export const SortableItemContext = createContext<SortableListContext>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

const SortableListItem = ({ style, id, infoPopover, children }: Props) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    active,
  } = useSortable({ id });
  const context = useMemo(
    () => ({ attributes, listeners, ref: setActivatorNodeRef }),
    [attributes, listeners, setActivatorNodeRef],
  );

  const mergedStyles: CSSProperties = {
    ...style,
    opacity: isDragging ? 0 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <SortableItemContext.Provider value={context}>
      <ListItem ref={setNodeRef} style={mergedStyles} id={id}>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-1 gap-1">
            {children}
            {infoPopover && (
              <InfoPopover
                title={infoPopover.title}
                body={infoPopover.body}
                onClick={(e) => e.preventDefault()}
              />
            )}
          </div>
          <DraggableHandle isActive={active?.id === id} />
        </div>
      </ListItem>
    </SortableItemContext.Provider>
  );
};

export default SortableListItem;
